// Wall collision against the active dungeon SDF (B1, monorail-fgy).
// Anything that moves is kept in the water: penetrating bodies are pushed
// back along the SDF gradient and their into-wall velocity is removed, so
// contact reads as sliding along a soft bank rather than a bounce.
//
// "Can I fit through this gap?" is literally `sdf(p) > -radius` — the
// squeeze-pore mechanic (B4 valves, grow-too-big gating) falls out of this
// same field later.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { getActiveDungeon } from '../dungeon/active'
import { layoutSdf, layoutSdfGrad } from '../dungeon/carve'

export function wallsSystem(world: GameWorld): void {
  const layout = getActiveDungeon()
  if (!layout) return

  for (const e of [...world.with('position', 'velocity', 'renderable')]) {
    // Absorbing bodies are mid-animation into the player; chain segments
    // follow their boss (handcrafted arenas own that constraint later).
    if (e.absorbing || e.chainSegment) continue

    const r = e.renderable.radius
    const s = layoutSdf(layout, e.position.x, e.position.y)
    if (s <= -r) continue // fully in open water

    // Projectiles splat on the bank instead of sliding along it.
    if (e.projectile) {
      world.remove(e)
      continue
    }

    resolveIntoWater(layout, e, r, s)
  }
}

function resolveIntoWater(
  layout: NonNullable<ReturnType<typeof getActiveDungeon>>,
  e: Entity,
  r: number,
  s: number,
): void {
  // Project back to the s = -r isoline. The smooth-union field is only
  // approximately unit-gradient, so iterate a few short steps rather than
  // trusting one jump (deep-in-wall spawns converge too — the gradient of a
  // min-distance field always points toward the nearest carved space).
  let grad = { x: 0, y: 0 }
  for (let i = 0; i < 4 && s > -r; i++) {
    grad = layoutSdfGrad(layout, e.position.x, e.position.y)
    const push = s + r
    e.position.x -= grad.x * push
    e.position.y -= grad.y * push
    s = layoutSdf(layout, e.position.x, e.position.y)
  }

  // Remove the velocity component pointing into the wall; keep the
  // tangential part so movement slides along the bank.
  const v = e.velocity!
  const vn = v.x * grad.x + v.y * grad.y
  if (vn > 0) {
    v.x -= grad.x * vn
    v.y -= grad.y * vn
  }
}
